import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import logo from '../assets/img/logo.png';
import deutsch from '../assets/img/deutsch.png';
import english from '../assets/img/english.png';

// Import all brochures and catalogues
import absorexCatalogueEN from '../assets/Absorex Catalogue English.pdf';
import absorexCatalogueDE from '../assets/Absorex Catalogue German.pdf';
import dekoBrochureEN from '../assets/DEKO brochure english.pdf';
import dekoBrochureDE from '../assets/DEKO brochure german.pdf';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isHeaderSticked, setIsHeaderSticked] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isMobilePpBagsOpen, setIsMobilePpBagsOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  // Get current language documents
  const getCurrentDocuments = () => {
    const isEnglish = i18n.language === 'en';
    return {
      catalogue: isEnglish ? absorexCatalogueEN : absorexCatalogueDE,
      brochure: isEnglish ? dekoBrochureEN : dekoBrochureDE
    };
  };

  const getCurrentLangPrefix = () => {
    const lang = i18n.language;
    return lang === 'en' ? '' : `/${lang}`;
  };

  const handleLanguageChange = (lang) => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.startsWith('/de') ? currentPath.substring(3) : currentPath;
    const newPath = lang === 'en' ? pathWithoutLang || '/' : `/${lang}${pathWithoutLang || '/'}`;
    i18n.changeLanguage(lang);
    navigate(newPath);
    setIsLanguageDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
    if (isMobileNavOpen) {
      closeAllMobileDropdowns();
    }
  };

  const closeAllMobileDropdowns = () => {
    setIsMobileProductsOpen(false);
    setIsMobilePpBagsOpen(false);
  };

  const toggleMobileProducts = (e) => {
    e.preventDefault();
    setIsMobileProductsOpen(!isMobileProductsOpen);
    if (isMobileProductsOpen) {
      setIsMobilePpBagsOpen(false);
    }
  };

  const toggleMobilePpBags = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobilePpBagsOpen(!isMobilePpBagsOpen);
  };

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      if (isMobileNavOpen) {
        body.classList.add('mobile-nav-active');
      } else {
        body.classList.remove('mobile-nav-active');
      }
    }
  }, [isMobileNavOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileNavOpen && !event.target.closest('.navbar.d-lg-none') && !event.target.closest('.mobile-nav-toggle')) {
        toggleMobileNav();
      }
      if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown')) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [isMobileNavOpen, isLanguageDropdownOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticked(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMobileLinkClick = () => {
    scrollToTop();
    if (isMobileNavOpen) {
      toggleMobileNav();
    }
  };

 const productLinks = {
   ppBags: {
    parent: { name: t("header.productLinks.ppBags"), path: "/products/pp-bags" },
    items: [
      { name: t("header.ppBagsItems.standard"), path: "/products/bags-standard" },
      { name: t("header.ppBagsItems.laminated"), path: "/products/bags-laminated" },
      { name: t("header.ppBagsItems.blockBottom"), path: "/products/bags-block-bottom" },
      { name: t("header.ppBagsItems.bopp"), path: "/products/bags-bopp" },
      { name: t("header.ppBagsItems.kraft"), path: "/products/bags-kraft" },
      { name: t("header.ppBagsItems.backSeal"), path: "/products/bags-back-seal" },
    ],
  },
  directLinks: [
    { name: t("header.productLinks.desiccants"), path: "/products/desiccants" },
    { name: t("header.productLinks.bigBag"), path: "/products/big-bag" },
    { name: t("header.productLinks.strapsLashing"), path: "/products/straps-lashing" },
    { name: t("header.productLinks.containerSafetySheets"), path: "/products/container-safety-sheet" },
  ]
};

  const renderProductSubMenu = (items, clickHandler) => {
    return items.map((link) => (
      <li key={link.name}>
        <Link to={`${getCurrentLangPrefix()}${link.path}`} onClick={clickHandler}>{link.name}</Link>
      </li>
    ));
  };

  const getCurrentLanguageFlag = () => {
    return i18n.language === 'en' ? english : deutsch;
  };

  const getCurrentLanguageName = () => {
    return i18n.language === 'en' ? 'English' : 'Deutsch';
  };

  const documents = getCurrentDocuments();

  return (
    <>
      <section id="topbar" className="d-flex align-items-center">
        <div className="container d-flex justify-content-between align-items-center">
          <div className="contact-info d-flex align-items-center">
            <a href={`mailto:${t('topbar.email')}`} className="topbar-item">
              <i className="fas fa-envelope"></i>
              <span>{t('topbar.email')}</span>
            </a>
            <a href={documents.catalogue} target="_blank" rel="noopener noreferrer" className="topbar-item brochure-link">
              <i className="fas fa-book"></i>
              <span>{t('topbar.catalogue', 'Catalogue')}</span>
            </a>
            <a href={documents.brochure} target="_blank" rel="noopener noreferrer" className="topbar-item brochure-link">
              <i className="fas fa-file-pdf"></i>
              <span>{t('topbar.brochure', 'Brochure')}</span>
            </a>
          </div>
          <div className="topbar-actions d-flex align-items-center">
            <a href="tel:+1234567890" className="topbar-item phone-link d-none d-md-flex">
              <i className="fas fa-phone"></i>
              <span>{t('topbar.phone', '+1 (234) 567-890')}</span>
            </a>
            <div className="topbar-social d-flex align-items-center">
              <a href="https://www.linkedin.com/company/deko-industriebedarf-gmbh" className="social-link" aria-label="LinkedIn">
                <i className="fab fa-linkedin-in"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <header id="header" className={`header ${isHeaderSticked ? 'sticked' : ''}`}>
        <div className="container d-flex align-items-center justify-content-between">
          <div className="logo">
            <Link to={`${getCurrentLangPrefix()}/`} onClick={scrollToTop}>
              <img src={logo} alt="Company Logo" className="img-fluid" width="70" height="70" />
            </Link>
          </div>

          <div className="d-flex align-items-center">
            <nav className="navbar d-none d-lg-block">
              <ul>
                <li><NavLink to={`${getCurrentLangPrefix()}/`} end onClick={scrollToTop}>{t('header.home')}</NavLink></li>
                <li><NavLink to={`${getCurrentLangPrefix()}/about`} onClick={scrollToTop}>{t('header.about')}</NavLink></li>
                <li><NavLink to={`${getCurrentLangPrefix()}/quality`} onClick={scrollToTop}>{t('header.quality')}</NavLink></li>

                <li className="dropdown">
                  <Link to={`${getCurrentLangPrefix()}/products`} onClick={scrollToTop}>{t('header.products')} <i className="fas fa-chevron-down"></i></Link>
                  <ul>
                    {renderProductSubMenu(productLinks.directLinks, scrollToTop)}
                    <li className="dropdown">
                      <Link to={`${getCurrentLangPrefix()}${productLinks.ppBags.parent.path}`} onClick={scrollToTop}>
                        {productLinks.ppBags.parent.name} <i className="fas fa-chevron-right"></i>
                      </Link>
                      <ul>{renderProductSubMenu(productLinks.ppBags.items, scrollToTop)}</ul>
                    </li>
                  </ul>
                </li>

                <li><NavLink to={`${getCurrentLangPrefix()}/industries`} onClick={scrollToTop}>{t('header.industries')}</NavLink></li>
                <li><NavLink to={`${getCurrentLangPrefix()}/contact`} onClick={scrollToTop}>{t('header.contact')}</NavLink></li>
              </ul>
            </nav>

            <div className={`language-dropdown ${isLanguageDropdownOpen ? 'active' : ''}`}>
              <button
                className="language-dropdown-toggle"
                onClick={toggleLanguageDropdown}
                aria-label="Select Language"
              >
                <img src={getCurrentLanguageFlag()} alt={getCurrentLanguageName()} width="20" height="14" />
                <span>{getCurrentLanguageName()}</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="language-dropdown-menu">
                <div
                  className={`language-dropdown-item ${i18n.language === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                >
                  <img src={english} alt="English" width="20" height="14" />
                  <span>English</span>
                </div>
                <div
                  className={`language-dropdown-item ${i18n.language === 'de' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('de')}
                >
                  <img src={deutsch} alt="Deutsch" width="20" height="14" />
                  <span>Deutsch</span>
                </div>
              </div>
            </div>

            <button type="button" className="mobile-nav-toggle d-lg-none" onClick={toggleMobileNav} aria-label="Toggle navigation">
              <i className={isMobileNavOpen ? "fas fa-times" : "fas fa-bars"}></i>
            </button>
          </div>
        </div>
      </header>

      <div className={`navbar-mobile ${isMobileNavOpen ? 'navbar-mobile-active' : ''}`}>
        <nav className="navbar d-lg-none">
          <ul>
            <li><NavLink to={`${getCurrentLangPrefix()}/`} end onClick={handleMobileLinkClick}>{t('header.home')}</NavLink></li>
            <li><NavLink to={`${getCurrentLangPrefix()}/about`} onClick={handleMobileLinkClick}>{t('header.about')}</NavLink></li>
            <li><NavLink to={`${getCurrentLangPrefix()}/quality`} onClick={handleMobileLinkClick}>{t('header.quality')}</NavLink></li>

            <li className="dropdown">
              <a href="#" onClick={toggleMobileProducts}>
                {t('header.products')} <i className={isMobileProductsOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
              </a>
              <ul className={isMobileProductsOpen ? 'dropdown-active' : ''}>
                {renderProductSubMenu(productLinks.directLinks, handleMobileLinkClick)}
                <li className="dropdown">
                  <a href="#" onClick={toggleMobilePpBags}>
                    {productLinks.ppBags.parent.name} <i className={isMobilePpBagsOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
                  </a>
                  <ul className={isMobilePpBagsOpen ? 'dropdown-active' : ''}>
                    {renderProductSubMenu(productLinks.ppBags.items, handleMobileLinkClick)}
                  </ul>
                </li>
              </ul>
            </li>

            <li><NavLink to={`${getCurrentLangPrefix()}/industries`} onClick={handleMobileLinkClick}>{t('header.industries')}</NavLink></li>
            <li><NavLink to={`${getCurrentLangPrefix()}/contact`} onClick={handleMobileLinkClick}>{t('header.contact')}</NavLink></li>
            
            {/* Mobile-specific topbar items */}
            <li className="mobile-topbar-divider"></li>
            <li>
              <a href={documents.catalogue} target="_blank" rel="noopener noreferrer" onClick={handleMobileLinkClick}>
                <i className="fas fa-book"></i> {t('topbar.catalogue', 'Catalogue')}
              </a>
            </li>
            <li>
              <a href={documents.brochure} target="_blank" rel="noopener noreferrer" onClick={handleMobileLinkClick}>
                <i className="fas fa-file-pdf"></i> {t('topbar.brochure', 'Brochure')}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Header;